import { Component, OnInit, Injector } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { PermissionAssigneeTargetLinkContainerModel, TenantPermissionAssigneeTargetService, TenantTeamRoleContainerModel, TenantTeamRoleService, TenantUserRoleContainerModel, TenantUserRoleService } from 'blg-akaun-ts-lib';
import { fetchAllByCriteria } from '../../../../../../listing.utils';
import * as moment from 'moment';
import { AkaunConfirmationDialogComponent } from '../../../../../../dialogues/akaun-confirmation-dialog/akaun-confirmation-dialog';
import { PermissionFacade } from '../../../../../../modules/permission/facades/permission.facade';
import { RoleModel } from '../../../../../../models/role-permissions.model';
import { AppConfig } from '../../../../../../visa';
import { zip } from 'rxjs';
import { map } from 'rxjs/operators';
import { SubSink } from 'subsink2';
import { PermissionStates } from '../../../../permission-controller/root';
import { RolePermActions } from '../../../../role-permissions-controller/actions';
import { RolePermSelectors } from '../../../../role-permissions-controller/selectors';
import { RolePermStates } from '../../../../role-permissions-controller/states';
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateReturn: boolean;
  selectedIndex: number;
  deactivateAdd: boolean;
  deactivateList: boolean;
}

@Component({
  selector: 'app-role-permission-details',
  templateUrl: './role-permission-details.component.html',
  styleUrls: ['./role-permission-details.component.scss']
})
export class RolePermissionDetailsComponent implements OnInit {

  public detailsForm: FormGroup;

  protected readonly index = 2;
  private localState: LocalState;

  // initialise local states
  readonly localState$ = this.viewColFacade.selectLocalState(this.index);

  // api visa
  apiVisa = AppConfig.apiVisa;

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;

  // selected role model
  role: RoleModel;

  // fields that require options
  status: string;
  statusArr: any = [];
  newStatusArr: any = [];

  // to confirm whether user wants to delete a role
  akaunConfirmationDialogComponentMatRef: MatDialogRef<AkaunConfirmationDialogComponent>;

  //translation variable
  role_name_text: string;
  role_code_text: string;
  description_text: string;
  created_by_text: string;
  updated_by_text: string;
  created_date_text: string;
  modified_date_text: string;
  not_a_valid_entry_text: string;
  status_text: string;
  active_text: string;
  inactive_text: string;
  delete_button: string;

  constructor(
    public dialog: MatDialog,
    private tenantPermAssigneeTargetLinkService: TenantPermissionAssigneeTargetService,
    private tenantTeamRoleService: TenantTeamRoleService,
    private tenantUserRoleService: TenantUserRoleService,
    private readonly store: Store<PermissionStates>,
    private viewColFacade: PermissionFacade,
    private componentStore: ComponentStore<{localState: LocalState}>,
    private injector: Injector
  ) {
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.role_name_text = res.role_name;
        this.role_code_text = res.role_code;
        this.description_text = res.description;
        this.created_by_text = res.created_by;
        this.updated_by_text = res.updated_by;
        this.created_date_text = res.created_date;
        this.modified_date_text = res.modified_date;
        this.not_a_valid_entry_text = res.invalid_entry;
        this.status_text = res.status;
        this.active_text = res.active;
        this.inactive_text = res.inactive;
        this.delete_button = res.delete_button;
      });
    } else {
      this.role_name_text = 'Role Name';
      this.role_code_text = 'Role Code';
      this.description_text = 'Description';
      this.created_by_text = 'Created By';
      this.updated_by_text = 'Updated By';
      this.created_date_text = 'Created Date';
      this.modified_date_text = 'Modified Date';
      this.not_a_valid_entry_text = 'Not a valid entry';
      this.status_text = 'Status';
      this.active_text = 'ACTIVE';
      this.inactive_text = 'INACTIVE';
      this.delete_button = 'DELETE';
    }
  }

  /**
   * Lifecycle method: Called when the component is first initialised.
   */
  ngOnInit(): void {
    this.statusArr = [
      { value: 'ACTIVE', viewValue: this.active_text },  // Use the translated value
      { value: 'INACTIVE', viewValue: this.inactive_text }  // Use the translated value
    ];
    // initial configurations for local state
    this.subSink.sink = this.localState$.subscribe( a => {
      this.localState = a;
      this.componentStore.setState( {localState: a} );
    });

    this.newStatusArr = this.statusArr;

    this.subSink.sink = this.store.select(RolePermSelectors.selectRoleDetails)
    .subscribe(({form, entity}) => {

      // set form values
      if (form && entity) {
        this.role = entity;
        this.detailsForm = new FormGroup({});
        Object.keys(form).forEach(i => {
          this.detailsForm.addControl(i, new FormControl())
        })
        this.detailsForm.patchValue({
          name: entity.app_mst_role.name,
          description: entity.app_mst_role.description,
          status: entity.app_mst_role.status,

          // uneditable
          code: entity.app_mst_role.code,
          createdDate: entity.app_mst_role.created_date ? moment(entity.app_mst_role.created_date).format('YYYY-MM-DD HH:MM:ss') : "Data not available...",
          updatedDate: entity.app_mst_role.updated_date ? moment(entity.app_mst_role.updated_date).format('YYYY-MM-DD HH:MM:ss') : "Data not available...",
          createdBy: entity.created_by_name ? entity.created_by_name : "Data not available...",
          updatedBy: entity.updated_by_name ? entity.updated_by_name : "Data not available..."
        })
      }

    })
  }

  /**
   * Dynamically change the options of shown on the screen by applying this filter.
   *
   * @param filterValue the value that the user types onto the screen
   */
   applyStatusFilter(filterValue: string) {
    filterValue = filterValue.trim(); // remove whitespace
    filterValue = filterValue.toLowerCase();  // MatTableDataSource defaults to lowercase matches
    this.newStatusArr = this.statusArr.filter( (option) =>
    option.toLowerCase().includes(filterValue) );
  }

  /**
   * This function is called when the user clicks on the delete button.
   * This will dispatch an action to delete the vendor.
   */
  onDelete() {
    // find all the perm assignee target links that are associated with this role
    const permAssigneeGuids$ = this.getPermAssigneeTargetGuids();
    const teamToRoleGuids$ = this.getTeamToRoleLinks();
    const userToRoleGuids$ = this.getUserToRoleLinks();

    this.subSink.sink = zip(permAssigneeGuids$, teamToRoleGuids$ , userToRoleGuids$)
      .subscribe(([permAssigneeGuids, teamToRoleGuids, userToRoleGuids]) => {
        const roleGuid = this.role.app_mst_role.guid.toString();

        if (permAssigneeGuids.length > 0 || teamToRoleGuids.length > 0 || userToRoleGuids.length > 0) {
          this.akaunConfirmationDialogComponentMatRef = this.dialog.open(
            AkaunConfirmationDialogComponent,
            {width: '400px'}
          );
          this.akaunConfirmationDialogComponentMatRef.componentInstance.confirmMessage = 'This role either has permissions, teams or users associated with it. Are you sure you want to delete?';
          this.akaunConfirmationDialogComponentMatRef
            .afterClosed()
            .subscribe(result => {
              if (result == true) {
                if (teamToRoleGuids.length > 0) {
                  // dispatch ngrx action to delete team to role links
                  this.store.dispatch(RolePermActions.removeTeamsFromRoleInit({guids: teamToRoleGuids}));
                }
                if (userToRoleGuids.length > 0) {
                  // dispatch ngrx action to delete user to role links
                  this.store.dispatch(RolePermActions.removeUsersFromRoleInit({guids: userToRoleGuids}));
                }
                if (permAssigneeGuids.length > 0) {
                  // dispatch ngrx action to delete permission assignee target links
                  this.store.dispatch(RolePermActions.deleteRoleWithPermsInit({permAssigneeGuids: permAssigneeGuids}));
                }
                // dispatch ngrx action to delete the role
                this.store.dispatch(RolePermActions.deleteRoleInit({guid: roleGuid}))
              }
            })
        } else {
          // dispatch ngrx action to delete the role
          this.store.dispatch(RolePermActions.deleteRoleInit({guid: roleGuid}))
        }
      })
  }

  /**
   * Return the guids of the permission assignee target links that are related to this role.
   *
   * @returns the guids of the permission assignee target links that are related to this role
   */
  getPermAssigneeTargetGuids() {
    /**
     * Replaces the raw SELECT that went through SubQueryService:
     *
     *   FROM app_perm_assignee_target_link hdr
     *   WHERE hdr.assignee_hdr_table = 'app_mst_role'
     *     AND hdr.assignee_hdr_guid = '<role guid>'
     *     AND hdr.status != 'DELETED'
     *
     * GET permission-assignee-target/query ->
     * PermissionAssigneeTargetLinkUow.generateWhereClauseByCriteria honours
     * `assignee_hdr_table` and `assignee_hdr_guid`, and defaults to
     * `status != 'DELETED'`.
     *
     * These guids drive a delete, so fetchAllByCriteria pages until the endpoint
     * stops returning full pages rather than stopping at one page and leaving
     * residual links behind.
     */
    return fetchAllByCriteria<PermissionAssigneeTargetLinkContainerModel>(
      pagination => this.tenantPermAssigneeTargetLinkService.getByCriteria(pagination, this.apiVisa),
      [
        { columnName: 'assignee_hdr_table', operator: '=', value: 'app_mst_role' },
        { columnName: 'assignee_hdr_guid', operator: '=', value: this.role.app_mst_role.guid.toString() }
      ]
    ).pipe(
      map(links => links.map(link => link.permAssigneeTargetLink.guid.toString()))
    )
  }

  /**
   * Returns the guids of the team to role links that are related to this role.
   *
   * @returns the guids of the team to role links that are related to this role
   */
  getTeamToRoleLinks() {
    /**
     * Replaces the raw SELECT that went through SubQueryService:
     *
     *   FROM app_mst_link_grp_to_role hdr
     *   WHERE hdr.mst_role_guid = '<role guid>' AND hdr.status != 'DELETED'
     *
     * `mst_role_guid` is the criterion the team-to-role link endpoint already
     * takes -- it is what role-permission-listing's getTeamsInRole sends -- and
     * the link UoW defaults to `status != 'DELETED'`.
     *
     * Paged in full: these guids drive a delete.
     */
    return fetchAllByCriteria<TenantTeamRoleContainerModel>(
      pagination => this.tenantTeamRoleService.getByCriteria(pagination, this.apiVisa),
      [{ columnName: 'mst_role_guid', operator: '=', value: this.role.app_mst_role.guid.toString() }]
    ).pipe(
      map(links => links.map(link => link.app_mst_link_grp_to_role.guid.toString()))
    )
  }

  /**
   * Returns the guids of the subject to to role links that are related to this role
   *
   * @returns the guids of the subject to role links that are related to this role
   */
  getUserToRoleLinks() {
    /**
     * Replaces the raw SELECT that went through SubQueryService:
     *
     *   FROM app_mst_link_subject_to_role hdr
     *   WHERE hdr.app_mst_role_guid = '<role guid>' AND hdr.status != 'DELETED'
     *
     * `app_mst_role_guid` is the criterion the user-to-role link endpoint already
     * takes -- it is what role-permission-listing's getUsersInRole sends -- and
     * the link UoW defaults to `status != 'DELETED'`.
     *
     * Paged in full: these guids drive a delete.
     */
    return fetchAllByCriteria<TenantUserRoleContainerModel>(
      pagination => this.tenantUserRoleService.getByCriteria(pagination, this.apiVisa),
      [{ columnName: 'app_mst_role_guid', operator: '=', value: this.role.app_mst_role.guid.toString() }]
    ).pipe(
      map(links => links.map(link => link.link_subject_to_role.guid.toString()))
    )
  }

  /**
   * Lifecycle method: Called when component is destroyed.
   */
   ngOnDestroy() {
    this.subSink.unsubscribe();
  }

}
